import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Icon from "../utils/icons";
import { selectStoreConfig, selectCategories } from "../redux/Slice/siteSlice";
import { selectProducts } from "../redux/Slice/productSlice";
import Category from "./Category";
import ProductCard from "./ProductCard";
import OfferDeals from "./OfferDeals";
import { ProductSkeletons } from "./Loader";
import { getFeaturedProducts, getHomeRows, getOfferProducts, getRecommended } from "../services/products";
import { OFFERS } from "../utils/ownezzy";
import { money, sizedImage } from "../utils/formatters";
import { recentlyViewed } from "../utils/recentlyViewed";
import useStoreType from "../utils/storeTypes";
import useReveal from "../hooks/useReveal";

const PhoneSkinStudio = React.lazy(() => import("./PhoneSkinStudio"));
const BlouseDesignStudio = React.lazy(() => import("./BlouseDesignStudio"));

const SLIDE_MS = 6000;

// Clothing stores' home page, laid out the way fashion sellers on Instagram present a drop: a full-width story-style
// banner slider (progress bars on top, slow zoom on the photo), a ticker, categories as story circles, the trending
// grid, "Picked for you" from what the shopper looked at, deals, a shop-by-category grid with photos, one row per
// category and a shoppable Instagram-style feed. Every text and photo comes from the owner's website editor and
// products, as on the other store types' Home.
export default function FashionHome({ go, browse, query, filtered, add, onSelect, cart, updateQty }) {
    const config = useSelector(selectStoreConfig);
    const cats = useSelector(selectCategories);
    const products = useSelector(selectProducts);
    const type = useStoreType();

    const [featured, setFeatured] = useState(null);
    const [home, setHome] = useState(null);
    const [offers, setOffers] = useState([]);
    const [forYou, setForYou] = useState(null);
    const seen = useMemo(() => recentlyViewed(config.slug), [config.slug]);

    useEffect(() => {
        let live = true;
        getFeaturedProducts().then((items) => live && setFeatured(items)).catch(() => live && setFeatured(false));
        getHomeRows().then((data) => live && setHome(data)).catch(() => live && setHome(false));
        getRecommended(seen, { limit: 8 }).then((items) => live && setForYou(items)).catch(() => live && setForYou([]));
        return () => { live = false; };
    }, [seen]);
    useEffect(() => {
        if (!config.hasOffers) return setOffers([]);
        let live = true;
        getOfferProducts().then((items) => live && setOffers(items)).catch(() => live && setOffers([]));
        return () => { live = false; };
    }, [config.hasOffers]);

    const featuredItems = featured || (featured === false ? products.slice(0, 12) : []);
    const totalProducts = home ? home.total : products.length;
    const rows = useMemo(
        () => (home ? home.rows : home === false ? cats.map((cat) => ({ category: cat, products: products.filter((p) => p.cat === cat) })) : []),
        [home, cats, products]
    );
    // Each category's cover: the photo of its first product
    const covers = useMemo(() => Object.fromEntries(rows.map((row) => [row.category, (row.products.find((p) => p.img) || {}).img])), [rows]);
    const feed = useMemo(() => rows.flatMap((row) => row.products.slice(0, 2)).filter((p) => p.img).slice(0, 8), [rows]);

    useReveal([featured, home, forYou, offers.length]);

    // The "Shop by" section: the owner's heading, chips and price tiles (Edit website -> Content), else the store
    // kind's. Chips default to the store's own categories (Ownezzy's demo stores keep their occasion chips).
    const shopBy = config.shopBy || {};
    const chips = (shopBy.items || []).length
        ? shopBy.items.map((x) => [x.icon || (type.categoryIcons || {})[x.name] || type.icon, x.name])
        : config.showOwnezzyOffer ? type.occasions || [] : cats.slice(0, 8).map((c) => [(type.categoryIcons || {})[c] || type.icon, c]);
    const budgets = (shopBy.budgets || []).length ? shopBy.budgets : type.budgets;

    const banners = config.heroBanners.length
        ? config.heroBanners
        : [{ title: config.name, description: config.about, image: (products[0] || {}).img || "" }];

    return (
        <div className="fashionHome">
            <FashionHero banners={banners} go={go} type={type} />

            {config.announcement && (
                <div className="fxTicker" aria-hidden="true">
                    <div className="fxTickerTrack">
                        {[0, 1].map((copy) => (
                            <span key={copy}>{config.announcement.split("•").map((part) => <em key={part}>{part.trim()} <b>✦</b></em>)}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Categories as Instagram story circles */}
            <nav className="fxStories" aria-label="Shop by category">
                {offers.length > 0 && (
                    <button type="button" className="fxStory isOffers" onClick={() => go("Products", OFFERS)}>
                        <span className="fxStoryRing"><span className="fxStoryPic fxStoryDeal">%</span></span>
                        <b>{config.offersTitle}</b>
                    </button>
                )}
                {cats.map((c, i) => (
                    <button type="button" key={c} className="fxStory" style={{ "--i": i }} onClick={() => go("Products", c)}>
                        <span className="fxStoryRing">
                            <span className="fxStoryPic">{covers[c] ? <img src={sizedImage(covers[c], 200)} alt="" loading="lazy" /> : type.icon}</span>
                        </span>
                        <b>{c}</b>
                    </button>
                ))}
            </nav>

            {/* Gift and selfcare stores: shop by occasion/scent (products tagged with it) and by budget (storeTypes.js) */}
            {!query && type.occasions && browse && !shopBy.off && chips.length > 0 && (
                <section className="fxSection fxOccasions">
                    <SectionHead eyebrow={shopBy.eyebrow || type.occasionsEyebrow || "🎉 Gifting made easy"} title={shopBy.title || type.occasionsTitle || "Shop by occasion"} text={shopBy.subtitle || type.occasionsSubtitle || "Find the right gift for every moment."} />
                    <div className="fxOccasionGrid">
                        {chips.map(([icon, name], i) => (
                            <button type="button" key={name} className="fxOccasion reveal" style={{ "--d": `${(i % 5) * 50}ms` }} onClick={() => (cats.includes(name) ? go("Products", name) : browse({ search: name }))}>
                                <span>{icon}</span><b>{name}</b>
                            </button>
                        ))}
                    </div>
                    {budgets && budgets.length > 0 && (
                        <div className="fxBudgets">
                            {budgets.map((amount, i) => (
                                <button type="button" key={amount} className={`fxBudget reveal tone${i}`} style={{ "--d": `${i * 70}ms` }} onClick={() => browse({ maxPrice: amount })}>
                                    <small>{shopBy.budgetPrefix || type.budgetPrefix || "Gifts under"}</small><b>{money(amount)}</b><Icon.ArrowRight />
                                </button>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Trust points the owner left switched on (Edit website -> Content) */}
            {!config.highlightsOff && (config.highlights || []).some((card) => !card.hidden) && <div className="fxTrust" style={{ "--n": (config.highlights || []).filter((card) => !card.hidden).length }}>
                {(config.highlights || []).filter((card) => !card.hidden).map((card, index) => (
                    <div key={index} className="reveal" style={{ "--d": `${index * 70}ms` }}>
                        <i>{card.icon}</i>
                        <span><b>{card.title}</b><small>{card.text}</small></span>
                    </div>
                ))}
            </div>}

            <React.Suspense fallback={null}>
                {!query && config.template === "tech" && (
                    <PhoneSkinStudio onSelect={onSelect} products={products} />
                )}

                {!query && config.template === "blouses" && (
                    <BlouseDesignStudio go={go} browse={browse} />
                )}
            </React.Suspense>

            {query ? (
                <Category cat="Search results" items={filtered} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} viewAll={() => go("Products", "All")} />
            ) : (
                <>
                    {(featured === null || featuredItems.length > 0) && (
                        <section className="fxSection">
                            <SectionHead eyebrow="🔥 This week" title={type.featuredTitle} text={type.featuredText} action={totalProducts > 0 ? `View all ${totalProducts}` : ""} onAction={() => go("Products", "All")} />
                            <div className="fxGrid">
                                {featured === null ? <ProductSkeletons count={8} /> : featuredItems.map((p, i) => (
                                    <div className="reveal" style={{ "--d": `${(i % 4) * 80}ms` }} key={p.id}>
                                        <ProductCard p={p} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} />
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {forYou && forYou.length > 0 && (
                        <section className="fxSection fxForYou">
                            <SectionHead eyebrow={<><Icon.Stars /> Smart picks</>} title={seen.length ? "Picked for you" : "Fresh this week"}
                                text={seen.length ? `${type.items.charAt(0).toUpperCase()}${type.items.slice(1)} like the ones you looked at, picked by our smart assistant.` : "New arrivals our shoppers are loving."} />
                            <div className="fxRail">
                                {forYou.map((p, i) => (
                                    <div className="fxRailItem reveal" style={{ "--d": `${i * 60}ms` }} key={p.id}>
                                        <ProductCard p={p} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} />
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {offers.length > 0 && (
                        <OfferDeals title={config.offersTitle} items={offers} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} viewAll={() => go("Products", OFFERS)} />
                    )}

                    {rows.length > 0 && (
                        <section className="fxSection">
                            <SectionHead eyebrow="🛍️ Collections" title="Shop by category" text={(config.spotlights || []).map((s) => s.title).join(" · ")} />
                            <div className="fxBento">
                                {rows.slice(0, 6).map((row, i) => (
                                    <button type="button" key={row.category} className={`fxTile reveal ${i === 0 ? "isBig" : ""}`} style={{ "--d": `${i * 70}ms` }} onClick={() => go("Products", row.category)}>
                                        {covers[row.category] && <img src={sizedImage(covers[row.category], 800)} alt="" loading="lazy" />}
                                        <span className="fxTileCopy">
                                            <b>{row.category}</b>
                                            <small>{row.total ?? row.products.length} {type.items} <Icon.ArrowRight /></small>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {rows.map((row) => (
                        <div className="reveal" key={row.category}>
                            <Category cat={row.category} items={row.products} total={row.total} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} viewAll={() => go("Products", row.category)} />
                        </div>
                    ))}

                    {feed.length >= 4 && (
                        <section className="fxSection fxFeed">
                            <SectionHead eyebrow={<><Icon.Instagram /> As seen on our Instagram</>} title="Shop the feed" text="Tap a look to shop it." />
                            <div className="fxFeedGrid">
                                {feed.map((p, i) => (
                                    <button type="button" key={p.id} className="fxFeedItem reveal" style={{ "--d": `${(i % 4) * 60}ms` }} onClick={() => onSelect(p)}>
                                        <img src={sizedImage(p.img, 500)} alt={p.name} loading="lazy" />
                                        <span className="fxFeedHover"><Icon.HeartFill /> <b>{p.name}</b><small>{money(p.price)}</small></span>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}

function SectionHead({ eyebrow, title, text, action, onAction }) {
    return (
        <div className="fxHead reveal">
            <div>
                <span className="fxEyebrow">{eyebrow}</span>
                <h2>{title}</h2>
                {text && <p>{text}</p>}
            </div>
            {action && <button type="button" className="fxHeadAction" onClick={onAction}>{action} <Icon.ArrowRight /></button>}
        </div>
    );
}

// The banner slider: one photo at a time, cross-fading with a slow zoom, Instagram-story progress bars on top.
// Pauses while a mouse is over it (a tap on a phone also sends "mouse enter" and never "leave", which stopped the
// slider for good, so only a real mouse pauses it), and for shoppers who ask for reduced motion. Tapping the banner
// (not its buttons) shows the next photo, like a story; swiping left or right moves either way.
function FashionHero({ banners, go, type }) {
    const [slide, setSlide] = useState(0);
    const [paused, setPaused] = useState(false);
    const swipeFrom = useRef(null);
    const count = banners.length;
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    useEffect(() => {
        if (paused || reduced || count < 2) return undefined;
        const timer = window.setTimeout(() => setSlide((s) => (s + 1) % count), SLIDE_MS);
        return () => window.clearTimeout(timer);
    }, [slide, paused, reduced, count]);

    const active = ((slide % count) + count) % count;
    const banner = banners[active] || {};
    const step = (by) => count > 1 && setSlide((s) => (s + by + count) % count);
    const onTap = (event) => {
        if (event.target.closest("button, a")) return;
        if (banner.imageOnly && banner.image) return go("Products", "All"); // no buttons on it: the picture is the link
        step(1);
    };
    const onTouchStart = (event) => { swipeFrom.current = event.touches[0].clientX; };
    const onTouchEnd = (event) => {
        if (swipeFrom.current === null) return;
        const moved = event.changedTouches[0].clientX - swipeFrom.current;
        swipeFrom.current = null;
        if (Math.abs(moved) > 40) {
            event.preventDefault(); // a swipe, not a tap: no click follows
            step(moved < 0 ? 1 : -1);
        }
    };

    return (
        // An "image only" banner (website editor) shows its picture without the text, buttons and dark shade over it;
        // a tap on it opens the products
        <section className={`fxHero ${paused ? "isPaused" : ""} ${banner.imageOnly && banner.image ? "isImageOnly" : ""}`} aria-roledescription="carousel" aria-label={banner.imageOnly && banner.title ? banner.title : "Featured collections"}
            onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
            onClick={onTap} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {banners.map((b, i) => (
                <div key={i} className={`fxHeroSlide ${i === active ? "isActive" : ""}`} aria-hidden={i !== active}>
                    {b.image ? (
                        // Phones get the banner's tall picture when it has one (the hero is tall and narrow there)
                        <picture>
                            {b.mobileImage && <source media="(max-width: 640px)" srcSet={b.mobileImage} />}
                            <img src={b.image} alt="" loading={i === 0 ? "eager" : "lazy"} />
                        </picture>
                    ) : (
                        // A banner without an image yet (a new store): a soft brand-coloured panel with a picture outline
                        <div className="fxHeroNoImage" aria-hidden="true"><span>🖼️</span><small>Banner image</small></div>
                    )}
                </div>
            ))}
            <div className="fxHeroShade" />
            {count > 1 && (
                <div className="fxHeroBars" role="tablist" aria-label="Slides">
                    {banners.map((_, i) => (
                        <button type="button" key={i} role="tab" aria-selected={i === active} aria-label={`Slide ${i + 1}`} onClick={() => setSlide(i)}
                            className={i < active ? "isDone" : i === active ? "isActive" : ""}>
                            <i key={i === active ? `a${slide}` : "x"} style={{ animationDuration: `${SLIDE_MS}ms` }} />
                        </button>
                    ))}
                </div>
            )}
            <div className="fxHeroCopy" key={active}>
                <span className="fxHeroEyebrow">{type.heroEyebrow}</span>
                <h1>{banner.title}</h1>
                {banner.description && <p>{banner.description}</p>}
                <div className="fxHeroBtns">
                    <button type="button" className="fxBtn" onClick={() => go("Products", "All")}>{type.heroCta} <Icon.ArrowRight /></button>
                    <button type="button" className="fxBtn isGhost" onClick={() => go("Products", "All")}>New arrivals</button>
                </div>
            </div>
            <div className="fxHeroBadge" aria-hidden="true">
                <span>{type.badgeTop}</span>
                <b>{type.badgeBottom}</b>
            </div>
            {count > 1 && (
                <div className="fxHeroArrows">
                    <button type="button" aria-label="Previous slide" onClick={() => setSlide((s) => (s - 1 + count) % count)}><Icon.ChevronLeft /></button>
                    <button type="button" aria-label="Next slide" onClick={() => setSlide((s) => (s + 1) % count)}><Icon.ChevronRight /></button>
                </div>
            )}
        </section>
    );
}
